/**
 * 顯示層的文字正規化：把站方「結構化系統訊息」在畫面上還原成人話。
 *
 * 為什麼需要（不是資料壞掉）：
 *  - `packages/protocol/src/ek21.ts` 的 `systemMessage` 處理器在 payload **不是字串** 時
 *    直接 `JSON.stringify(payload)`，所以像 `{message:"熱烈歡迎 … 駕到！"}` 這種站方事件
 *    會變成一整串 `{"message":"…"}` 原樣進到事件佇列，UI 把它當純文字畫出來。
 *  - 站方內嵌圖用 `img:<32hex>` 這種**文字標記**（見 notes/static-batch-A.md A5），
 *    直接印出來就是裸露 hash；這裡在顯示層換成「［圖片］」，不露出 hash。
 *  - 有些訊息尾端會殘留序列化留下的 `"`（例：`剛進來"`），或夾著控制字元。
 *
 * 邊界：
 *  - **只改顯示**。`packages/worker`／`packages/protocol` 的資料保持原樣，事件佇列裡仍是
 *    站方原文；要原文請直接看事件，不要從這個函式回推。
 *  - 純函式、可重複套用（第二次不會再變）。正常中文標點（「」、！、？、…）一律不動。
 */
/** `img:<hex>` 的內嵌圖標記；站方是 32 位小寫 hex，這裡放寬到 16–64 位以免漏接。 */
const IMG_MARKER = /img:([0-9a-f]{16,64})/gi;
/** 已經包成 `[img]…[/img]`／`[bigimg]…[/bigimg]` 的圖，連標籤一起收掉。 */
const IMG_BLOCK = /\[(?:big)?img(?:\s*=\s*\d{1,4})?\][\s\S]*?\[\/(?:big)?img\]/gi;
/** 換掉內嵌圖後留在句子裡的字（給人看，不給 CSS／DOM 任何東西）。 */
export const IMAGE_PLACEHOLDER = '［圖片］';
/** 站方系統訊息裡可能裝人話的欄位（順序＝優先序）。 */
const MESSAGE_KEYS = [
    'message',
    'msg',
    'text',
    'content',
    'messageText',
    'message_text',
    'msg_text',
    'body',
];
/** 結構化事件常見的外層容器（`{data:{message:…}}`、`{payload:…}`）。 */
const ENVELOPE_KEYS = ['data', 'payload', 'event', 'detail'];
/** 最多拆幾層 JSON 字串／外殼；超過就當拆不掉（避免惡意資料無限遞迴）。 */
const MAX_UNWRAP_DEPTH = 4;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * 從已 parse 的 JSON 值取出「要顯示的那句話」。
 * 回 `null` ＝這不是我們認得的系統訊息外殼（呼叫端保留原文）。
 */
function textFromParsed(value, depth) {
    if (typeof value === 'string')
        return value;
    if (depth >= MAX_UNWRAP_DEPTH)
        return null;
    if (Array.isArray(value)) {
        for (const item of value) {
            const text = textFromParsed(item, depth + 1);
            if (text !== null && text.trim() !== '')
                return text;
        }
        return null;
    }
    if (!isRecord(value))
        return null;
    for (const key of MESSAGE_KEYS) {
        const candidate = value[key];
        if (typeof candidate === 'string' && candidate.trim() !== '')
            return candidate;
        // `{message:{text:"…"}}` 這種再包一層的也接得住。
        if (isRecord(candidate) || Array.isArray(candidate)) {
            const text = textFromParsed(candidate, depth + 1);
            if (text !== null && text.trim() !== '')
                return text;
        }
    }
    for (const key of ENVELOPE_KEYS) {
        const inner = value[key];
        if (isRecord(inner) || Array.isArray(inner)) {
            const text = textFromParsed(inner, depth + 1);
            if (text !== null && text.trim() !== '')
                return text;
        }
    }
    return null;
}
/**
 * 拆掉「整串就是一個 JSON」的外殼；不是就回 `null`。
 *
 * 只認**開頭就是** `{`／`[`／`"` 的字串：正常發言不會這樣開頭，而 JSON.parse 失敗時
 * （例如 `{大家晚安}`）會回 null，原文照樣保留，不會誤刪。
 */
function unwrapJson(text) {
    const trimmed = text.trim();
    if (trimmed === '')
        return null;
    const head = trimmed[0];
    if (head !== '{' && head !== '[' && head !== '"')
        return null;
    let parsed;
    try {
        parsed = JSON.parse(trimmed);
    }
    catch {
        return null;
    }
    return textFromParsed(parsed, 0);
}
/**
 * `JSON.parse` 失敗時的救援：後端可能在傳輸中被截斷（預覽、log 切片），
 * 留下 `{"message":"熱烈歡迎 …` 這種半截。
 * 只在前綴明確是 message 欄位時動手；擷取後把殘留的 `"`／`"}` 留在下一段一起清。
 */
const PARTIAL_ENVELOPE = /^\{\s*"(?:message|msg|text|content)"\s*:\s*"([\s\S]*)$/;
function unescapePartial(body) {
    return body
        .replace(/\\u([0-9a-f]{4})/gi, (_match, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '')
        .replace(/\\t/g, '\t');
}
/** 殘留的序列化尾巴：收尾的 `}`／`]`，以及前面帶一個逗號的情況。 */
function stripEnvelopeTail(text) {
    return text.replace(/\s*["'`]?\s*[}\]]\s*$/, '');
}
/** 出現幾次某個引號。 */
function countChar(text, char) {
    let count = 0;
    for (const item of text)
        if (item === char)
            count += 1;
    return count;
}
/**
 * 尾端清理：控制字元、行尾空白、落單的引號。
 *
 * 引號只在**落單**（總數是奇數）時才砍，或整串被同一對引號包住時脫掉外層；
 * 所以 `他說「你好」`、`他說"你好"`、`夥伴們"嗨"` 都不會被動到。
 */
function cleanTail(input) {
    let text = input
        // Cc 控制字元（保留 \t \n），以及常見的零寬／BOM 垃圾。
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200D\uFEFF]/g, '')
        .replace(/\r\n?/g, '\n')
        // 行尾空白（含 NBSP）不留。
        .replace(/[ \t\u00A0]+$/gm, '')
        .trim();
    // 整串被同一對引號包住（序列化殘留）：脫掉外層，內部的引號不動。
    for (const quote of ['"', "'"]) {
        if (text.length >= 2 &&
            text.startsWith(quote) &&
            text.endsWith(quote) &&
            countChar(text, quote) === 2) {
            text = text.slice(1, -1).trim();
        }
    }
    // 落單的尾端引號：總數奇數才代表它沒有配對（正常引號一定成對）。
    for (const quote of ['"', "'"]) {
        while (text.endsWith(quote) && countChar(text, quote) % 2 === 1) {
            text = text.slice(0, -1).trimEnd();
        }
    }
    return text;
}
/**
 * 把站方訊息正規化成「可以直接畫在畫面上」的一句話。
 *
 * 處理順序：拆 JSON 外殼 → 收掉 `img:<hash>` → 清控制字元／尾部殘留。
 * 純文字（含正常中文標點與引號）原樣回傳；任何一步不確定就不動，寧可少改。
 */
export function normalizeMessageText(input) {
    if (typeof input !== 'string' || input === '')
        return '';
    let text = input;
    for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth += 1) {
        const unwrapped = unwrapJson(text);
        if (unwrapped === null)
            break;
        text = unwrapped;
    }
    // parse 不掉的半截 JSON：至少把外殼切掉，別讓 `{"message":"` 出現在畫面上。
    const partial = PARTIAL_ENVELOPE.exec(text.trim());
    if (partial?.[1] !== undefined) {
        text = stripEnvelopeTail(unescapePartial(partial[1]));
    }
    // 先收整塊的圖，再收裸標記；順序反了會留下 `[img]［圖片］[/img]`。
    text = text.replace(IMG_BLOCK, IMAGE_PLACEHOLDER).replace(IMG_MARKER, IMAGE_PLACEHOLDER);
    return cleanTail(text);
}
/**
 * 給 `<title>`／`aria-label` 用的原文摘要：正規化後截短，讓 hover 還看得出是哪一則。
 * 不顯示 hash（正規化已收掉），所以即使貼上去也不會洩漏媒體識別碼。
 */
export function messageTextSummary(input, limit = 120) {
    const text = normalizeMessageText(input);
    if (limit <= 0 || text.length <= limit)
        return text;
    return `${text.slice(0, limit)}…`;
}
//# sourceMappingURL=message-text.js.map